import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { DirectionsCarOutlined, MapOutlined, PlaceOutlined } from "@mui/icons-material";
import MapView from "../MapView/MapView";
import { DASH, cardSx } from "../../dashboard/tokens";
import { normalizeCoords } from "../../../../Utils/googleMapsImageUrls";

type Props = {
  latitude?: unknown;
  longitude?: unknown;
  city?: string;
  state?: string;
  addressLabel?: string;
};

/**
 * Small map card under Quick Actions (Task 3): the pin, the city, and links
 * out to Google Maps for the full map and directions.
 */
const MapCard: React.FC<Props> = ({ latitude, longitude, city, state, addressLabel }) => {
  const coords = normalizeCoords(latitude, longitude);
  const place = [city, state].filter(Boolean).join(", ");
  const query = coords ? `${coords.lat},${coords.lng}` : addressLabel || place;
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;

  return (
    <Box sx={{ ...cardSx, overflow: "hidden" }} data-testid="map-card">
      {coords ? (
        <Box sx={{ "& > div": { border: "none !important", borderRadius: "0 !important" } }}>
          <MapView latitude={coords.lat} longitude={coords.lng} />
        </Box>
      ) : (
        <Stack alignItems="center" justifyContent="center" sx={{ height: 140, bgcolor: DASH.cream, color: DASH.muted }}>
          <MapOutlined />
          <Typography variant="caption">Map unavailable for this address</Typography>
        </Stack>
      )}
      <Stack spacing={2} sx={{ px: 4, py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <PlaceOutlined sx={{ color: DASH.teal, fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1d2b2b" }} noWrap>
            {place || "Location"}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button
            size="small"
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DirectionsCarOutlined sx={{ fontSize: "16px !important" }} />}
            sx={{ color: DASH.teal, fontWeight: 700, whiteSpace: "nowrap" }}
          >
            Directions
          </Button>
          <Button
            size="small"
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            sx={{ borderColor: DASH.border, color: DASH.teal, fontWeight: 700, whiteSpace: "nowrap" }}
            data-testid="view-on-map"
          >
            View on Map
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default MapCard;
